<template>
  <div>
    <div
      class="d-flex align-items-center mb-2"
      style="gap: 8px;"
    >
      <select
        v-model="status"
        class="form-control"
        style="max-width: 200px;"
        @change="onFilterChange"
      >
        <option value="imported">
          Imported
        </option>
        <option value="not_imported">
          Not Imported
        </option>
        <option value="all">
          All
        </option>
      </select>
      <select
        v-model="processed"
        class="form-control"
        style="max-width: 200px;"
        @change="onFilterChange"
      >
        <option value="all">
          All
        </option>
        <option value="original">
          Original
        </option>
        <option value="filtered">
          Filtered
        </option>
        <option value="pre_processed">
          Pre-processed
        </option>
      </select>
    </div>

    <table class="table">
      <thead>
        <tr>
          <th scope="col">
            Date
          </th>
          <th scope="col">
            Time
          </th>
          <th scope="col">
            Type
          </th>
          <th scope="col">
            Status
          </th>
          <th scope="col">
            Layout
          </th>
          <th scope="col">
            Settings
          </th>
          <th scope="col">
            Actions
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="history in histories"
          :key="rowKey(history)"
          class="history-row"
        >
          <td>{{ formatDate(history.created_at) }}</td>
          <td>{{ formatTime(history.time) }}</td>
          <td>{{ history.type || "—" }}</td>
          <td>
            <span
              class="badge"
              :class="history.source === 'not_imported' ? 'badge-danger' : 'badge-success'"
            >
              {{ history.source === 'not_imported' ? 'Not Imported' : 'Imported' }}
            </span>
          </td>
          <td>
            <a
              v-if="history.layout"
              :href="`/banner/${history.layout.customer_id}/group/${history.layout_id}/show`"
              target="_blank"
            >
              {{ history.layout_id }}
            </a>
            <span v-else>{{ history.layout_id }} (deleted)</span>
          </td>
          <td>{{ history?.mapping?.name || "N/A" }}</td>
          <td>
            <div class="dropdown">
              <a
                href="#"
                role="button"
                data-toggle="dropdown"
                class="action"
              >
                <b-icon
                  icon="three-dots-vertical"
                  class="text-body font-2xl"
                />
              </a>
              <div class="dropdown-menu">
                <template v-if="history.source === 'not_imported'">
                  <button
                    class="dropdown-item"
                    @click="viewError(history)"
                  >
                    <i class="bi bi-exclamation-triangle" />&nbsp;View error
                  </button>
                </template>
                <template v-else>
                  <button
                    class="dropdown-item"
                    @click="downloadFile(history.file_path)"
                  >
                    <i class="bi bi-cloud-arrow-down" />&nbsp;Download
                  </button>
                  <button
                    v-if="!disableRerun"
                    class="dropdown-item"
                    @click="rerun(history)"
                  >
                    <i class="bi bi-repeat" />&nbsp;Rerun
                  </button>
                  <a
                    v-if="qaTestsUrl && history.mapping?.id"
                    class="dropdown-item"
                    :href="`${qaTestsUrl}?import_history=${history.source_id}`"
                    target="_blank"
                  >
                    <i class="bi bi-clipboard-check" />&nbsp;Create QA test
                  </a>
                </template>
              </div>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <b-pagination
      v-model="currentPage"
      :total-rows="pagination.total"
      :per-page="5"
      align="center"
      first-number
      last-number
      @input="fetchHistories"
    />
  </div>
</template>

<script>
import {
  IMPORT_HISTORIES_EVENTS,
  importHistoriesEventBroker,
} from "@frontend/group/modules/import-histories/event-broker";

export default {
  name: "ImportHistoriesTable",
  props: {
    layoutId: { type: [Number, String], default: null },
    disableRerun: { type: Boolean, default: false },
    qaTestsUrl: { type: String, default: "" },
  },
  data() {
    return {
      currentPage: 1,
      status: "imported",
      processed: "all",
      histories: [],
      pagination: {
        current_page: 1,
        last_page: 1,
        total: 0,
      },
      fetchSequence: 0,
    };
  },
  mounted() {
    importHistoriesEventBroker.on(
      IMPORT_HISTORIES_EVENTS.FETCH_HISTORIES,
      () => {
        this.fetchHistories();
      },
      true
    );
  },
  methods: {
    onFilterChange() {
      this.currentPage = 1;
      this.fetchHistories();
    },
    rowKey(history) {
      return history.source === "not_imported"
        ? `not_imported_${history.source_id}_${history.layout_id}`
        : `imported_${history.source_id}`;
    },
    async fetchHistories() {
      const requestSequence = ++this.fetchSequence;

      try {
        const params = {
          page: this.currentPage,
          status: this.status,
          processed: this.processed,
        };
        if (this.layoutId) {
          params.layout_id = this.layoutId;
        }

        const response = await axios.get("/import-history/fetch-histories", { params });

        // A newer fetch (filter/page change) started while this one was in
        // flight — drop this response so it can't overwrite fresher state.
        if (requestSequence !== this.fetchSequence) {
          return;
        }

        if (response.data.status === "success") {
          this.histories = response.data.data;
          this.pagination = response.data.pagination;
        }
      } catch (error) {
        console.log("Error fetching histories:", error);
      }
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "2-digit",
      });
    },
    formatTime(timeString) {
      return new Date(`2000-01-01 ${timeString}`).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    },
    async downloadFile(filePath) {
      try {
        const { data } = await axios.get("/import-history/download-file", {
          params: { file_path: filePath },
          responseType: "blob",
        });

        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filePath.split("/").pop());
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

      } catch (error) {
        console.log("Error downloading file:", error);
        toastr.error("Failed to download the file.");
      }
    },
    viewError(history) {
      toastr.error(history.error_message || "No error details available.", "", { escapeHtml: true });
    },
    rerun(history) {
      const mapping = history?.mapping ?? null;

      if (history.type === 'feed') {
        importHistoriesEventBroker.fire(IMPORT_HISTORIES_EVENTS.RERUN_FEED_IMPORT, {
          mapping: mapping,
          file_path: history.file_path
        });
      }

      if (history.type === 'gtin') {
        importHistoriesEventBroker.fire(IMPORT_HISTORIES_EVENTS.RERUN_GTIN_IMPORT, {
          mapping: mapping,
          file_path: history.file_path
        });
      }
    },
  },
};
</script>

<style scoped>
</style>
